import React, { useEffect, useRef, useState, useCallback } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import Overlay from 'ol/Overlay';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, transformExtent } from 'ol/proj';
import { Style, Fill, Stroke, Text } from 'ol/style';
import { MapPinIcon } from '@heroicons/react/24/outline';
import client from '../../lib/feathers';

const geojsonFormat = new GeoJSON({
  dataProjection: 'EPSG:4326',
  featureProjection: 'EPSG:3857',
});

const COLORS = [
  'rgba(99, 8, 1, 0.25)',    // primary
  'rgba(59, 130, 246, 0.25)', // blue
  'rgba(16, 185, 129, 0.25)', // green
  'rgba(245, 158, 11, 0.25)', // amber
  'rgba(139, 92, 246, 0.25)', // purple
  'rgba(236, 72, 153, 0.25)', // pink
];

const STROKES = [
  '#630801',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
];

/**
 * Read-only map viewer for appellation boundaries.
 * Reloads visible appellations on pan/zoom.
 *
 * Props:
 *   - appellationId: string (optional) - Show a single appellation (no dynamic reload)
 */
export default function AppellationMapViewer({ appellationId, height = '400px' }) {
  const mapRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapInstance = useRef(null);
  const vectorSourceRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadForExtent = useCallback(async (map, vectorSource) => {
    try {
      setLoading(true);
      const service = client.service('admin/appellation');

      let appellations;
      if (appellationId) {
        const result = await service.get(appellationId);
        appellations = result.boundary ? [result] : [];
      } else {
        // Get current viewport in EPSG:4326
        const extent = map.getView().calculateExtent(map.getSize());
        const [west, south, east, north] = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');

        // $simplify is REQUIRED, not an optimisation. Since v1.203.0 a find
        // does not return the boundary column at all, because returning many
        // MULTIPOLYGONs at once took production down twice. Without it every
        // feature here parses to nothing and the map draws empty.
        //
        // $simplify rather than $withBoundary: it swaps in server-simplified
        // GeoJSON, so 200 polygons stay bounded in size. $withBoundary at this
        // limit would be the original outage.
        //
        // Tolerance in degrees, looser when zoomed out. Mirrors
        // simplifyToleranceForZoom in client/src/lib/appellationMapLayer.js;
        // keep the two in step.
        const zoom = map.getView().getZoom() || 0;
        let simplify = 0.05;
        if (zoom >= 16) simplify = 0.0005;
        else if (zoom >= 14) simplify = 0.001;
        else if (zoom >= 12) simplify = 0.005;
        else if (zoom >= 10) simplify = 0.02;

        const result = await service.find({
          query: {
            $bbox: `${west},${south},${east},${north}`,
            $simplify: simplify,
            $limit: 200,
            $sort: { name: 1 },
          }
        });
        appellations = result.data;
      }

      vectorSource.clear();

      // Parse all features and compute extent area for z-ordering
      const allFeatures = [];
      appellations.forEach((a, i) => {
        if (!a.boundary) return;
        try {
          const features = geojsonFormat.readFeatures(a.boundary);
          features.forEach(f => {
            f.set('name', a.name);
            f.set('designationType', a.designationType);
            f.set('country', a.country);
            f.set('colorIndex', i);
            // Compute extent area for sorting
            const ext = f.getGeometry().getExtent();
            const area = (ext[2] - ext[0]) * (ext[3] - ext[1]);
            f.set('_area', area);
          });
          allFeatures.push(...features);
        } catch {
          // Skip invalid geometries
        }
      });

      // Sort largest first so smaller appellations render on top
      allFeatures.sort((a, b) => b.get('_area') - a.get('_area'));
      vectorSource.addFeatures(allFeatures);

      // Zoom to fit features when in single-appellation mode
      if (appellationId && allFeatures.length > 0) {
        const extent = vectorSource.getExtent();
        map.getView().fit(extent, { padding: [40, 40, 40, 40], maxZoom: 14 });
      }
    } catch (e) {
      console.error('Error loading appellations for map:', e);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [appellationId]);

  useEffect(() => {
    if (!mapRef.current) return;

    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attributions: '&copy; OpenStreetMap contributors',
          }),
        }),
        new VectorLayer({
          source: vectorSource,
          declutter: true,
          style: (feature) => {
            const colorIndex = feature.get('colorIndex') || 0;
            return new Style({
              fill: new Fill({ color: COLORS[colorIndex % COLORS.length] }),
              stroke: new Stroke({
                color: STROKES[colorIndex % STROKES.length],
                width: 2,
              }),
              text: new Text({
                text: feature.get('name'),
                font: '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fill: new Fill({ color: '#1f2937' }),
                stroke: new Stroke({ color: '#ffffff', width: 3 }),
                overflow: true,
              }),
            });
          },
        }),
      ],
      view: new View({
        center: fromLonLat([-98.5, 39.8]),
        zoom: 4,
      }),
    });

    // Hover tooltip for nested region names
    const tooltip = new Overlay({
      element: tooltipRef.current,
      positioning: 'bottom-center',
      offset: [0, -10],
    });
    map.addOverlay(tooltip);

    map.on('pointermove', (e) => {
      const features = [];
      map.forEachFeatureAtPixel(e.pixel, f => { features.push(f); });
      if (features.length > 0) {
        features.sort((a, b) => a.get('_area') - b.get('_area'));
        tooltip.setPosition(e.coordinate);
        tooltipRef.current.innerHTML = features
          .map((f, i) => {
            const name = f.get('name') || '';
            return i === 0
              ? `<strong>${name}</strong>`
              : `<span style="color:#6b7280">${name}</span>`;
          })
          .join('<br/>');
        tooltipRef.current.style.display = 'block';
      } else {
        tooltipRef.current.style.display = 'none';
      }
    });

    mapInstance.current = map;

    // Only auto-load in single-appellation mode
    if (appellationId) {
      loadForExtent(map, vectorSource);
    }

    return () => {
      map.setTarget(null);
      mapInstance.current = null;
    };
  }, [appellationId, loadForExtent]);

  const handleLoadClick = () => {
    if (mapInstance.current && vectorSourceRef.current) {
      loadForExtent(mapInstance.current, vectorSourceRef.current);
    }
  };

  return (
    <div>
      {!appellationId && (
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-500">Zoom and pan to the area you're interested in, then load appellations.</p>
          <button
            type="button"
            onClick={handleLoadClick}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
          >
            <MapPinIcon className="h-4 w-4 mr-1.5" />
            {hasLoaded ? 'Reload Area' : 'Load Appellations'}
          </button>
        </div>
      )}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10 pointer-events-none">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}
        <div
          ref={mapRef}
          className="w-full rounded-md border border-gray-300"
          style={{ height }}
        />
        <div
          ref={tooltipRef}
          className="bg-white rounded-md shadow-lg px-4 py-2 text-sm border border-gray-200 pointer-events-none whitespace-nowrap"
          style={{ display: 'none', position: 'absolute' }}
        />
      </div>
    </div>
  );
}
